import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Business as BusinessIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import { getOrganizationForJoinRequest } from '../services/joinRequestService';

function JoinOrganizationPage() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { user, status } = useSelector((state) => state.user);
  
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchOrganization = async () => {
      try {
        if (!orgId) {
          setError('Invalid organization link');
          setLoading(false);
          return;
        }

        const orgData = await getOrganizationForJoinRequest(orgId);
        setOrganization(orgData);
      } catch (err) {
        console.error('Error fetching organization:', err);
        setError('Organization not found');
      } finally {
        setLoading(false);
      }
    };

    fetchOrganization();
  }, [orgId]);

  useEffect(() => {
    // If user is already logged in and has an organization, redirect appropriately
    if (status !== 'loading' && user) {
      if (user.organization) {
        // User already has an organization, redirect to rooms
        navigate('/rooms');
      } else {
        // User is logged in but has no organization, redirect to org choice with context
        navigate('/organization-choice', { 
          state: { 
            targetOrgId: orgId, 
            targetOrgName: organization?.name 
          } 
        });
      }
    }
  }, [user, status, navigate, orgId, organization]);

  const handleRegister = () => {
    navigate(`/register?orgId=${orgId}&orgName=${encodeURIComponent(organization.name)}`);
  };

  const handleLogin = () => {
    navigate(`/login?orgId=${orgId}&orgName=${encodeURIComponent(organization.name)}`);
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Paper sx={{ p: 4 }} elevation={2}>
          <Alert severity="error">
            {error}
          </Alert>
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button variant="outlined" onClick={() => navigate('/')}>
              Go to Home
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  if (!organization) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Paper sx={{ p: 4 }} elevation={2}>
          <Alert severity="error">
            Organization not found
          </Alert>
        </Paper>
      </Container>
    );
  }

  // Show invitation page for non-logged-in users
  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper sx={{ p: 4 }} elevation={2}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <BusinessIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" component="h1" gutterBottom>
            You've been invited to join
          </Typography>
          <Typography variant="h5" color="primary" gutterBottom>
            {organization.name}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
            To join this organization, you need to create an account or log in.
            After registration, your join request will be submitted to the organization admins for approval.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<PersonAddIcon />}
            onClick={handleRegister}
            sx={{ minWidth: 200 }}
          >
            Create Account & Join
          </Button>
          
          <Button
            variant="outlined"
            size="large"
            onClick={handleLogin}
            sx={{ minWidth: 200 }}
          >
            Already have an account? Login
          </Button>
        </Box>

        <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            <strong>What happens next?</strong><br />
            1. Create your account or log in<br />
            2. Your join request will be automatically submitted<br />
            3. Wait for organization admin approval<br />
            4. Start using the meeting room system!
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
}

export default JoinOrganizationPage; 